import type { Situacao } from "@base/contracts";
import { formatar } from "../../dashboard";

export const ROTULO_DA_SITUACAO: Readonly<Record<Situacao, string>> = {
  ok: "OK",
  "aviso-precoce": "Aviso precoce",
  cronico: "Crônico",
  urgente: "Urgente",
  "pouco-volume": "Pouco volume",
};

export const ROTULO_DA_PLATAFORMA = {
  meta: "Meta",
  google: "Google",
} as const;

/** Primeira letra maiúscula, o resto como veio. */
export function frase(texto: string): string {
  const limpo = texto.trim();
  if (limpo.length === 0) return limpo;
  return limpo[0]!.toLocaleUpperCase("pt-BR") + limpo.slice(1);
}

const QUEM_AGE: Readonly<Record<string, string>> = {
  trafego: "Tráfego",
  tráfego: "Tráfego",
  midia: "Mídia",
  mídia: "Mídia",
  comercial: "Comercial",
  ninguem: "Ninguém",
  ninguém: "Ninguém",
};

export function rotuloDeQuemAge(valor: string): string {
  const chave = valor.trim().toLocaleLowerCase("pt-BR");
  return QUEM_AGE[chave] ?? (chave.length === 0 ? "—" : frase(valor));
}

const ETAPA_DA_SAFRA: Readonly<Record<string, string>> = {
  lead: "Lead",
  mql: "MQL",
  agendado: "Agendado",
  consulta: "Consulta",
  venda: "Venda",
};

export function rotuloDaEtapa(nome: string): string {
  return ETAPA_DA_SAFRA[nome.trim().toLocaleLowerCase("pt-BR")] ?? frase(nome);
}

export function rotuloDoSinal(sinal: string): string {
  const limpo = sinal.trim();
  if (limpo.toLocaleLowerCase("pt-BR") === "ok") return "OK";
  return frase(limpo);
}

export function rotuloDaSituacaoOuFrase(texto: string): string {
  return ROTULO_DA_SITUACAO[texto as Situacao] ?? frase(texto);
}

/** Termos da fila, no sentido do protótipo: curto, para ler no rodapé. */
export const LEGENDA: readonly (readonly [termo: string, texto: string])[] = [
  ["Urgente", "Pior que a base e fora da meta. Entra no dia de otimização."],
  ["Crônico", "Fora da meta, sem piora recente. Vai para a revisão mensal."],
  ["Aviso precoce", "Pior que a base, ainda dentro da meta."],
  ["Pouco volume", "Menos de 20 resultados. A linha não vira alerta."],
  ["Em jogo", "Reais da semana que separam o resultado da meta."],
];

/** "3 urgentes · 1 crônico · 1 sem volume": só as faixas com conta. */
export function resumoDaFila(contagem: {
  readonly urgentes: number;
  readonly cronicos: number;
  readonly avisos: number;
  readonly semVolume: number;
}): string {
  const partes = [
    [contagem.urgentes, "urgente", "urgentes"],
    [contagem.cronicos, "crônico", "crônicos"],
    [contagem.avisos, "aviso", "avisos"],
    [contagem.semVolume, "sem volume", "sem volume"],
  ] as const;
  const texto = partes
    .filter(([quantidade]) => quantidade > 0)
    .map(([quantidade, um, varios]) => `${formatar.quantidade(quantidade)} ${quantidade === 1 ? um : varios}`)
    .join(" · ");
  return texto || "Nenhuma conta na fila";
}

const numero = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });

/** Centavos inteiros do contrato → reais, no mesmo formato do dashboard. */
export function moedaDeCentavos(centavos: number | null): string {
  return formatar.moeda(centavos === null ? null : centavos / 100);
}

export function rotuloDeNumero(valor: number): string {
  return numero.format(valor);
}

/**
 * `YYYY-MM-DD` vira `DD/MM/AAAA` sem `Date`: meia-noite UTC
 * cairia no dia anterior no fuso de São Paulo.
 */
export function rotuloDeData(valor: string): string {
  const soDia = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  if (soDia) return `${soDia[3]}/${soDia[2]}/${soDia[1]}`;
  return formatar.data(valor);
}

/** `YYYY-MM-DD` vira `DD/MM`, no mesmo recorte de `rotuloDeData` (sem `Date`). */
export function rotuloDeDiaMes(valor: string): string {
  const soDia = /^(\d{4})-(\d{2})-(\d{2})/.exec(valor);
  if (soDia) return `${soDia[3]}/${soDia[2]}`;
  return rotuloDeData(valor);
}

export type SentidoDaVariacao = "pior" | "melhor" | "neutro";

/**
 * Lê o rótulo já formatado (`+60%`, `−4%`, `—`).
 *
 * ponytail: `+` conta como piora porque a métrica padrão da Central é custo
 * (CPL). O detalhe não traz `maiorEhMelhor`; CTR fica invertido até o contrato
 * marcar o sentido.
 */
export function leituraDaVariacao(rotulo: string): { readonly texto: string; readonly sentido: SentidoDaVariacao } {
  const limpo = rotulo.trim();
  if (limpo === "" || limpo === "—" || limpo === "-") return { texto: "— sem comparação", sentido: "neutro" };
  if (limpo === "0%" || limpo === "+0%" || limpo === "-0%" || limpo === "−0%") {
    return { texto: "0% estável", sentido: "neutro" };
  }
  if (limpo.startsWith("+")) return { texto: `${limpo} pior`, sentido: "pior" };
  if (limpo.startsWith("-") || limpo.startsWith("−")) return { texto: `${limpo} melhor`, sentido: "melhor" };
  return { texto: limpo, sentido: "neutro" };
}

export function tomDoSinal(sinal: string): "sucesso" | "atencao" | "perigo" | "neutro" {
  const texto = sinal.trim().toLocaleLowerCase("pt-BR");
  if (texto === "ok") return "sucesso";
  if (texto.startsWith("sem ")) return "neutro";
  if (texto.includes("acima") || texto.includes("abaixo") || texto.includes("fraco")) return "perigo";
  return "atencao";
}
